/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import CrossIcon from '@atlaskit/icon/core/cross';
import RefreshIcon from '@atlaskit/icon/core/refresh';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import type CardClient from '@atlaskit/link-provider/client';
import { SmartCardProvider } from '@atlaskit/link-provider/provider';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import { Card } from '@atlaskit/smart-card';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';

import { messages } from '../../../common/messages';

import type { SmartCardProviderProduct } from './smart-card-product';
import { formatSourceLabel } from './source-label';
import type { ManagedBlockSource, ManagedBlockSourceInvalidReason } from './types';

const sourceChipListStyles = css({
	display: 'flex',
	flexWrap: 'wrap',
	gap: token('space.050'),
	listStyle: 'none',
	marginTop: token('space.100'),
	marginRight: 0,
	marginBottom: 0,
	marginLeft: 0,
	minWidth: 0,
	paddingTop: 0,
	paddingRight: 0,
	paddingBottom: 0,
	paddingLeft: 0,
});

const sourceChipListInputStyles = css({
	marginTop: 0,
	marginBottom: token('space.100'),
});

const sourceChipStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	boxSizing: 'border-box',
	maxWidth: '100%',
	minWidth: 0,
	gap: token('space.050'),
	backgroundColor: token('color.background.neutral'),
	borderRadius: token('radius.small'),
	color: token('color.text.subtle'),
	paddingTop: token('space.050'),
	paddingRight: token('space.050'),
	paddingBottom: token('space.050'),
	paddingLeft: token('space.075'),
});

const sourceChipInvalidStyles = css({
	backgroundColor: token('color.background.danger'),
	borderColor: token('color.border.danger'),
	borderStyle: 'solid',
	borderWidth: token('border.width'),
	color: token('color.text.danger'),
});

const sourceChipResolvingStyles = css({
	opacity: 0.8,
});

const sourceChipLabelStyles = css({
	minWidth: 0,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

const sourceInvalidMessageStyles = css({
	flexShrink: 0,
	paddingLeft: token('space.050'),
	whiteSpace: 'nowrap',
});

const sourceResolvedCardStyles = css({
	flexGrow: 1,
	flexShrink: 1,
	minWidth: 0,
	overflow: 'hidden',
});

const sourceStatusStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	flexGrow: 1,
	flexShrink: 1,
	gap: token('space.050'),
	minWidth: 0,
});

type SourceListProps = {
	'aria-label': string;
	cardClient: CardClient;
	isDisabled?: boolean;
	onRemove: (sourceUrl: string) => void;
	onRetry: (sourceUrl: string) => void;
	smartCardProviderProduct?: SmartCardProviderProduct;
	sources: ManagedBlockSource[];
	spacing?: 'default' | 'input';
	testId?: string;
};

type SourceItemProps = {
	isDisabled: boolean;
	onRemove: (sourceUrl: string) => void;
	onRetry: (sourceUrl: string) => void;
	source: ManagedBlockSource;
};

const getInvalidSourceMessageKey = (
	invalidReason?: ManagedBlockSourceInvalidReason,
): keyof typeof messages => {
	switch (invalidReason) {
		case 'invalid-url':
			return 'sourceInvalidUrl';
		case 'not-found':
			return 'sourceNotFound';
		case 'unauthenticated':
		case 'unauthorized':
			return 'sourceUnauthorized';
		case 'unavailable':
		default:
			return 'sourceUnavailable';
	}
};

const SourceItem = ({ isDisabled, onRemove, onRetry, source }: SourceItemProps): JSX.Element => {
	const intl = useIntl();
	const sourceLabel = formatSourceLabel(source.url);
	const invalidMessageKey = getInvalidSourceMessageKey(source.invalidReason);

	return (
		<li
			css={[
				sourceChipStyles,
				source.status === 'invalid' && sourceChipInvalidStyles,
				source.status === 'resolving' && sourceChipResolvingStyles,
			]}
		>
			{source.status === 'resolved' && (
				<span css={sourceResolvedCardStyles}>
					<Card
						appearance="inline"
						placeholder={sourceLabel}
						resolvingPlaceholder={sourceLabel}
						url={source.url}
					/>
				</span>
			)}
			{source.status === 'resolving' && (
				<span css={sourceStatusStyles}>
					<Spinner label={intl.formatMessage(messages.sourceResolving)} size="small" />
					<span css={sourceChipLabelStyles}>{sourceLabel}</span>
				</span>
			)}
			{source.status === 'invalid' && (
				<span css={sourceStatusStyles}>
					<StatusErrorIcon label="" color="currentColor" size="small" />
					<span css={sourceChipLabelStyles}>{sourceLabel}</span>
					<span css={sourceInvalidMessageStyles}>
						<Text color="color.text.danger">{intl.formatMessage(messages[invalidMessageKey])}</Text>
					</span>
				</span>
			)}
			<Inline space="space.025" alignBlock="center">
				{source.status === 'invalid' && (
					<IconButton
						appearance="subtle"
						icon={RefreshIcon}
						isDisabled={isDisabled}
						label={intl.formatMessage(messages.retrySourceLabel, {
							source: sourceLabel,
						})}
						onClick={() => onRetry(source.url)}
						spacing="compact"
					/>
				)}
				<IconButton
					appearance="subtle"
					icon={CrossIcon}
					isDisabled={isDisabled}
					label={intl.formatMessage(messages.removeSourceLabel, {
						source: sourceLabel,
					})}
					onClick={() => onRemove(source.url)}
					spacing="compact"
				/>
			</Inline>
		</li>
	);
};

export const SourceList = ({
	'aria-label': ariaLabel,
	cardClient,
	isDisabled = false,
	onRemove,
	onRetry,
	smartCardProviderProduct,
	spacing = 'default',
	sources,
	testId,
}: SourceListProps): JSX.Element | null => {
	if (sources.length === 0) {
		return null;
	}

	return (
		<SmartCardProvider client={cardClient} product={smartCardProviderProduct}>
			<ul
				aria-label={ariaLabel}
				css={[sourceChipListStyles, spacing === 'input' && sourceChipListInputStyles]}
				data-testid={testId}
			>
				{sources.map((source) => (
					<SourceItem
						key={source.url}
						isDisabled={isDisabled}
						onRemove={onRemove}
						onRetry={onRetry}
						source={source}
					/>
				))}
			</ul>
		</SmartCardProvider>
	);
};
